/**
 * Whether anyone is signed in, and how to become so.
 *
 * The app had no answer to either question. Every route assumed Authgate's
 * cookie already existed, so a visitor arriving without one got "Couldn't load
 * today. Error: your session has expired" and no way forward — the app was
 * reachable only by someone who had already authenticated somewhere else.
 *
 * The flow is Authgate's email code, the same one the phone uses. The app never
 * handles a token: `/auth/code/verify` replies with `Set-Cookie`, the browser
 * stores it, and because Vite and the production vhost both serve Authgate
 * under this origin the cookie comes back on its own. Signing out goes through Authgate too, so
 * the refresh token is revoked rather than merely forgotten.
 *
 * The answer also has to be allowed to change. It was decided once, at mount,
 * which is fine for a visitor arriving cold and wrong for a tab left open: a
 * session that dies under a running app left every screen showing its own
 * refusal — "Couldn't load that day. Error: your session has expired" — beneath
 * a nav bar that still looked signed in, with nothing on the page to click.
 * `expired` below is that same event noticed once, centrally, and answered by
 * the sign-in screen rather than by seven error lines.
 */

import { SessionExpired } from "@calorio/api-client";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { signInWithPasskey } from "../lib/passkeys";
import { useApi } from "./providers";
import { clearSetupDone } from "./setup-done";

export type SessionStatus = "loading" | "signed-in" | "signed-out";

export type CodeRequest = "sent" | "not-invited";

interface Session {
  status: SessionStatus;
  /**
   * Whether the session ended on its own rather than by being signed out.
   *
   * Only the sign-in screen reads it, and only to say why it is there. The
   * distinction matters to the person: arriving cold is "sign in", being thrown
   * out of a half-written day is "sign in *again*", and a screen that cannot
   * tell the two apart reads as an app that lost what they were doing.
   */
  expired: boolean;
  /**
   * Ask Authgate to mail a six-digit code. "not-invited" means Calorio does not
   * admit the address, so no code is coming.
   */
  requestCode(email: string): Promise<CodeRequest>;
  /** Exchange the code for a session. False means the code was not accepted. */
  verifyCode(email: string, code: string): Promise<boolean>;
  /**
   * Sign in with a passkey on this device. Throws what the ceremony threw,
   * an `AbortError` when `signal` took it back.
   */
  passkeySignIn(signal?: AbortSignal): Promise<void>;
  signOut(): Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

/** Same-origin, so the Set-Cookie sticks. See vite.config.ts and the Caddy vhost. */
async function post(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [expired, setExpired] = useState(false);
  /**
   * Whether the session is being ended on purpose.
   *
   * A ref rather than state because the subscription below reads it from a
   * closure that is set up once, and because nothing renders differently for
   * it — it only decides how a 401 arriving in the next moment is described.
   */
  const leaving = useRef(false);
  /**
   * Whether this tab had a working session a moment ago.
   *
   * A 401 on behalf of an account nobody has is not a session running out, but
   * it arrives here as the same `SessionExpired`. The shell used to ask for the
   * profile and the overview above the gate that decides what to render — a
   * component's hooks all run before its first return — so every signed-out
   * visitor produced two of them, and the first screen a new account ever saw
   * read "your session expired. Sign in again to pick up where you left off —
   * everything you logged is still here". `SignedIn` in `App.tsx` is where that
   * was fixed: nothing is asked on an account's behalf until there is one.
   *
   * This stays because the provider cannot know that. It answers for whatever
   * is mounted above it, and the next query put there would say the same wrong
   * thing. Only a session that was answering can stop answering.
   *
   * Mirrored from `status` rather than set at each sign-in, so it cannot drift
   * from the thing it is about; a ref for the same reason as `leaving`.
   */
  const wasSignedIn = useRef(false);
  const api = useApi();
  const queryClient = useQueryClient();

  useEffect(() => {
    wasSignedIn.current = status === "signed-in";
  }, [status]);

  // On a cold load the access cookie has usually expired while the refresh
  // cookie has not, so this probe is also what triggers the silent renewal: the
  // client answers the 401 by refreshing and retrying once, and only a genuinely
  // dead session reaches the catch.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/v1/me")
      .then(() => !cancelled && setStatus("signed-in"))
      .catch((error) => {
        if (cancelled) return;
        // Only a dead session signs you out. A network failure must not: you
        // cannot complete a sign-in offline either, so bouncing to that screen
        // would strand the user on the one page that needs connectivity.
        setStatus(error instanceof SessionExpired ? "signed-out" : "signed-in");
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  /**
   * The same verdict, reached later — while the app is being used.
   *
   * Every screen's first act is a query, so the moment a session dies the
   * refusals arrive together: the day, the overview, the profile, whatever the
   * user had open. React Query's two caches are the one place all of them pass
   * through, so this subscribes there rather than asking each screen to notice
   * its own 401 and all seven to agree on what to do about it. The client has
   * already tried a refresh and failed by the time `SessionExpired` is thrown —
   * see `@calorio/api-client` — so there is nothing left to try here.
   *
   * An `ApiError` that is not a `SessionExpired` is somebody else's problem:
   * a 500 or a dead network still belongs on the screen that asked, where the
   * data it was asking for is, and throwing the user at a sign-in form they do
   * not need would lose the page they were on for nothing.
   */
  useEffect(() => {
    const expire = (error: unknown) => {
      if (!(error instanceof SessionExpired)) return;
      // Nothing was alive to expire — see `wasSignedIn`.
      if (!wasSignedIn.current) return;
      /*
       * Not when we asked for it. `signOut` revokes the refresh token and then
       * clears the cache, which sends anything still mounted at a cookie that
       * has just stopped working — so a `SessionExpired` lands here a moment
       * later, from the sign-out completing rather than from a session running
       * out underneath somebody. Pressing Sign out used to get you the apology:
       * "your session expired, sign in again to pick up where you left off".
       * Both halves of that are wrong, and the second one is almost taunting.
       *
       * The shell's own queries come down with the session now, so in the app
       * today nothing is left to ask — but, as with `wasSignedIn`, that is a
       * fact about `App.tsx` and this provider cannot see it.
       */
      if (leaving.current) return;
      setExpired(true);
      setStatus("signed-out");
    };

    // 'error' rather than 'failed': the latter fires per attempt, and a query
    // that is still retrying has not given up yet.
    const queries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") expire(event.action.error);
    });
    const mutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") expire(event.action.error);
    });

    return () => {
      queries();
      mutations();
    };
  }, [queryClient]);

  /**
   * What was cached under the old session, dropped — as signing out does it.
   *
   * Not an optimisation: this browser may be handed to somebody else, and the
   * next person to sign in must not be shown a frame of the last person's diary
   * while their own loads. It runs in an effect rather than from the
   * subscription above so that the shell has already been replaced by the
   * sign-in screen: by now no observer is mounted to refetch straight into the
   * same 401.
   *
   * The write queue stays, deliberately. It is this same person's unsent food,
   * it is stalled rather than lost (`drainQueue` keeps a 401 in the queue), and
   * it drains by itself when they sign back in.
   *
   * So does the first-run answer, for the same reason, and that is why this is
   * only half of what `signOut` does even though the two look like they ought
   * to match. A session running out is the *same person* — somebody who skipped
   * setup and whose cookie lapsed overnight would be thrown back into the form
   * the moment they re-authenticate. Only leaving on purpose hands the browser
   * on, and only `signOut` clears it.
   */
  useEffect(() => {
    if (expired) queryClient.clear();
  }, [expired, queryClient]);

  const requestCode = useCallback(async (email: string): Promise<CodeRequest> => {
    // 403 is Authgate saying Calorio does not admit this address (since
    // 2026-10-14). Anything else, rate limiting included, is its one "a code is
    // on its way" answer: whether the mail went out is not ours to know.
    const response = await post("/auth/code/request", { email: email.trim().toLowerCase() });
    return response.status === 403 ? "not-invited" : "sent";
  }, []);

  const passkeySignIn = useCallback(async (signal?: AbortSignal) => {
    await signInWithPasskey(signal);
    // The cookies are set by now, as after a verified code.
    setExpired(false);
    // Signed in again, so the next 401 is a real one and says so.
    leaving.current = false;
    setStatus("signed-in");
  }, []);

  const verifyCode = useCallback(async (email: string, code: string) => {
    const response = await post("/auth/code/verify", {
      email: email.trim().toLowerCase(),
      code: code.trim(),
    });
    if (!response.ok) return false;
    // The cookies are set by now; there is nothing to store.
    setExpired(false);
    // Signed in again, so the next 401 is a real one and says so.
    leaving.current = false;
    setStatus("signed-in");
    return true;
  }, []);

  const signOut = useCallback(async () => {
    // Before the request, not after: the refusals this causes can arrive while
    // it is still in flight.
    leaving.current = true;
    try {
      await post("/session/logout", {});
    } finally {
      // Leaving on purpose, so the sign-in screen has nothing to apologise for.
      setExpired(false);
      // Signed out locally regardless: if the call failed the cookie may
      // survive, but leaving someone staring at a screen that refuses to log
      // out is worse than a session that expires on its own.
      queryClient.clear();
      // And the first-run answer with it: the next account to sign in on this
      // browser must not inherit the last one's "I skipped" and land on an
      // app with no budget and no explanation of how to get one.
      clearSetupDone();
      setStatus("signed-out");
    }
  }, [queryClient]);

  const value = useMemo<Session>(
    () => ({ status, expired, requestCode, verifyCode, passkeySignIn, signOut }),
    [status, expired, requestCode, verifyCode, passkeySignIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error("useSession must be used inside <SessionProvider>");
  return session;
}
